import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Show, SignIn } from "@clerk/react";
import ClerkAuthProvider from "@/components/auth/ClerkAuthProvider";
import { Toaster } from "@/components/ui/sonner";
import { UserProvider, useUser } from "@/context/UserContext";
import { Header } from "@/components/Header";
import { Gate, RegistrationRedirect } from "@/components/Gate";
import { CONTACT_EMAIL } from "@/components/Delivery";
import Home from "@/pages/Home";
import SearchPage from "@/pages/SearchPage";
import ListingDetail from "@/pages/ListingDetail";
import CreateListing from "@/pages/CreateListing";
import Dashboard from "@/pages/Dashboard";
import Admin from "@/pages/Admin";
import { LoginPage, RegisterPage } from "@/pages/AuthForms";

const Private = ({ children }) => (
  <>
    <Show when="signed-in">{children}</Show>
    <Show when="signed-out"><Navigate to="/accedi" replace /></Show>
  </>
);

const AdminOnly = ({ children }) => {
  const { me } = useUser();
  if (!me) return null;
  if (me.role !== "admin") return <div className="py-24 text-center text-slate-600" data-testid="publish-forbidden">Solo l'amministratore può pubblicare veicoli.</div>;
  return children;
};

const TicketSignIn = () => {
  const { pathname, search } = useLocation();
  if (pathname === "/sign-in" && !search.includes("__clerk_ticket")) return <Navigate to="/accedi" replace />;
  return <AuthPage><SignIn routing="path" path="/sign-in" signUpUrl="/registrati" /></AuthPage>;
};

const AuthPage = ({ children }) => <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4">{children}</div>;

function App() {
  return (
    <div className="App min-h-screen bg-slate-50 flex flex-col">
      <BrowserRouter>
        <ClerkAuthProvider>
          <UserProvider>
            <Header />
            <RegistrationRedirect />
            <main className="flex-1">
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/cerca" element={<Gate><SearchPage /></Gate>} />
                <Route path="/veicolo/:id" element={<Gate><ListingDetail /></Gate>} />
                <Route path="/vendi" element={<Private><AdminOnly><CreateListing /></AdminOnly></Private>} />
                <Route path="/modifica/:id" element={<Private><AdminOnly><CreateListing /></AdminOnly></Private>} />
                <Route path="/completa-registrazione" element={<Gate><Navigate to="/cerca" replace /></Gate>} />
                <Route path="/area-personale" element={<Gate><Dashboard /></Gate>} />
                <Route path="/admin" element={<Private><Admin /></Private>} />
                <Route path="/sign-in/*" element={<TicketSignIn />} />
                <Route path="/sign-up/*" element={<Navigate to="/registrati" replace />} />
                <Route path="/accedi" element={<LoginPage />} />
                <Route path="/registrati" element={<RegisterPage />} />
                <Route path="*" element={<div className="py-24 text-center" data-testid="not-found-page"><p className="font-heading text-3xl font-bold">Pagina non trovata</p><a href="/" className="text-blue-600 mt-3 inline-block">Torna alla home</a></div>} />
              </Routes>
            </main>
            <footer className="border-t border-slate-200 bg-white">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-sm text-slate-500 flex justify-between">
                <span>© {new Date().getFullYear()} BuyNow</span>
                <span className="flex flex-col sm:flex-row sm:gap-4 text-right">
                  <span>Veicoli per privati e rivenditori</span>
                  <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-600 hover:text-blue-700" data-testid="footer-contact-email">{CONTACT_EMAIL}</a>
                </span>
              </div>
            </footer>
            <Toaster position="top-center" richColors />
          </UserProvider>
        </ClerkAuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
